import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import moment from 'moment';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

@Component({
    selector: 'sales-details-dialog',
    template: `
    <h2 mat-dialog-title>{{dialogTitle}} for {{data.account_manager}}</h2>
    <div mat-dialog-content>
      @if (data.error) {
        <div class="error-message">
          {{data.error}}
        </div>
      }
    
      @if (!data.error && (!data.roles || data.roles.length === 0)) {
        <div class="no-data">
          No details available for this account manager in the selected date range.
        </div>
      }
    
      @if (data.roles && data.roles.length > 0) {
        <div class="table-responsive">
          <table class="table table-striped">
            <thead>
              <tr>
                @for (header of tableHeaders; track header) {
                  <th>{{header}}</th>
                }
              </tr>
            </thead>
            <tbody>
              @for (row of tableRows; track row) {
                <tr>
                  @for (cell of row; track cell) {
                    <td>{{cell}}</td>
                  }
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    </div>
    <div mat-dialog-actions align="end">
      <button mat-button mat-dialog-close>Close</button>
      @if (data.roles && data.roles.length > 0) {
        <button mat-raised-button color="accent" (click)="downloadExcel()">
          <mat-icon>download</mat-icon> Download
        </button>
      }
    </div>
    `,
    styles: [`
    .no-data {
      padding: 20px;
      text-align: center;
      font-style: italic;
    }
    .error-message {
      padding: 20px;
      text-align: center;
      color: #f44336;
      font-weight: bold;
    }
    table {
      width: 100%;
    }
  `],
    standalone: false
})
export class SalesDetailsDialogComponent implements OnInit {
  dialogTitle: string = 'Details';
  tableHeaders: string[] = [];
  tableRows: string[][] = [];

  constructor(
    public dialogRef: MatDialogRef<SalesDetailsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    console.log('SalesDetailsDialogComponent initialized with data:', data);
    if (data && data.roles) {
      console.log('Roles data type:', typeof data.roles);
      console.log('Is array:', Array.isArray(data.roles));
      console.log('Length:', data.roles.length);
      if (data.roles.length > 0) {
        console.log('First role:', data.roles[0]);
      }
    }
  }

  ngOnInit() {
    // Log the data structure for debugging
    if (this.data && this.data.roles && this.data.roles.length > 0) {
      console.log(`Detail type: ${this.data.detail_type}`);
      console.log('First item in roles array:', this.data.roles[0]);
      console.log('Available fields:', Object.keys(this.data.roles[0]));
    }
    
    this.setupDialogContent();
  }

  setupDialogContent() {
    // Set dialog title
    this.dialogTitle = this.getDialogTitle();
    
    // Set table headers
    this.tableHeaders = this.getTableHeaders();
    
    // Generate table rows
    this.tableRows = this.generateTableRows();
  }

  getDialogTitle(): string {
    if (!this.data.detail_type || this.data.detail_type === 'roles') {
      return 'New Roles';
    } else if (this.data.detail_type === 'resume_submitted') {
      return 'Resume Submitted';
    } else if (this.data.detail_type === 'first_interview') {
      return 'First Interviews';
    } else if (this.data.detail_type === 'selected') {
      return 'Selected Candidates';
    } else if (this.data.detail_type === 'started') {
      return 'Started Candidates';
    }
    return 'Details';
  }

  getTableHeaders(): string[] {
    if (!this.data.detail_type || this.data.detail_type === 'roles') {
      return ['Role Title', 'Client', 'Created Date', 'Days Posted'];
    } else {
      // For all other detail types, use the same headers
      return ['Candidate Name', 'Role Title', 'Client'];
    }
  }

  generateTableRows(): string[][] {
    const rows: string[][] = [];
    
    if (!this.data.roles || this.data.roles.length === 0) {
      return rows;
    }
    
    // For New Roles, keep the existing functionality
    if (!this.data.detail_type || this.data.detail_type === 'roles') {
      this.data.roles.forEach(item => {
        rows.push([
          item.title || '',
          item.client || '',
          this.formatDate(item.open_date),
          (item.days_posted ? item.days_posted + ' days' : '')
        ]);
      });
      return rows;
    }
    
    // For other detail types (resume_submitted, first_interview, selected, started)
    // The data structure is the same for all these types
    this.data.roles.forEach(item => {
      // Log the item to see its structure
      console.log('Detail item:', item);
      
      rows.push([
        item.candidate_name || '',
        item.role_title || '',
        item.client || item.role_title || '' // Use client if available, otherwise use role_title
      ]);
    });
    
    return rows;
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    return moment(dateString).format('MMM D, YYYY');
  }

  downloadExcel(): void {
    console.log('Downloading Excel file');
    
    // Create data for Excel export
    const excelData: any[][] = [];
    
    // Add header row
    excelData.push(this.tableHeaders);
    
    // Add data rows
    this.tableRows.forEach(row => {
      excelData.push(row);
    });
    
    // Create worksheet
    const ws: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(excelData);
    
    // Set column widths
    const colWidths = [
      { wch: 30 }, // First column (Title or Candidate Name)
      { wch: 30 }, // Second column (Client or Role Title)
      { wch: 25 }, // Third column (Client)
      { wch: 15 }  // Fourth column (Date)
    ];
    ws['!cols'] = colWidths;
    
    // Create workbook
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, this.dialogTitle);
    
    // Generate Excel file
    const excelBuffer: any = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    
    // Save file
    const fileName = `${this.data.account_manager}_${this.dialogTitle}_${this.data.start_date}_to_${this.data.end_date}.xlsx`;
    const data: Blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(data, fileName);
  }
}
